/**
 * URL utilities for CIPA Fácil EHS
 * Handles dynamic URL generation based on environment
 */

/**
 * Gets the base URL for the application
 * Uses window.location.origin in browser, or falls back to env variable
 *
 * @returns string - The base URL (e.g., "https://cipa.ehsfacil.com" or "http://localhost:3000")
 */
export function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return process.env.REACT_APP_URL || "https://cipa.ehsfacil.com";
}

/**
 * Generates the public voting URL for an election
 *
 * @param empresaSlug - The company slug for the election
 * @returns string - The full voting URL
 */
export function getVotingUrl(empresaSlug: string): string {
  return `${getBaseUrl()}/${empresaSlug}`;
}

/**
 * Generates the public candidate registration URL for an election
 *
 * @param empresaSlug - The company slug for the election
 * @returns string - The full candidate registration URL
 */
export function getCandidateRegistrationUrl(empresaSlug: string): string {
  return `${getBaseUrl()}/candidato/${empresaSlug}`;
}

/**
 * Generates a shareable link with optional parameters
 *
 * @param path - The path after the base URL
 * @param params - Optional query parameters
 * @returns string - The full URL with parameters
 */
export function generateShareableLink(
  path: string,
  params?: Record<string, string>,
): string {
  const url = new URL(path, getBaseUrl());

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
}

/**
 * Extracts the slug from a URL path
 *
 * @param path - The URL path (e.g., "/candidato/empresa-slug" or "/empresa-slug")
 * @returns string | null - The extracted slug or null if not found
 */
export function extractSlugFromPath(path: string): string | null {
  const segments = path.split("/").filter(Boolean);

  // If path is like /candidato/slug, return the last segment
  if (segments.length >= 1) {
    return segments[segments.length - 1];
  }

  return null;
}

/**
 * Checks if the current environment is development/preview
 *
 * @returns boolean - True if running in development or preview mode
 */
export function isDevelopmentOrPreview(): boolean {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("vercel.app") ||
    hostname.includes("preview")
  );
}
